import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

async function apiFetch(path, { method = "POST", body, headers } = {}) {
  const base = process.env.REACT_APP_API_BASE;
  if (!base) throw new Error("Missing REACT_APP_API_BASE in .env");

  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error("Missing ID token (please sign out/in again)");

  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg.includes("HTTP") ? msg : `${msg} (HTTP ${res.status})`);
  }
  return data;
}

function qsValue(name) {
  const p = new URLSearchParams(window.location.search);
  return p.get(name) || "";
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function WarrantyFinalizePage() {
  const wrId = useMemo(() => qsValue("wrId").trim(), []);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [purpleFormNumber, setPurpleFormNumber] = useState("");

  const [status, setStatus] = useState(null); // {ready, finalPdfStatus, downloads}
  const stopRef = useRef(false);

  async function fetchStatusOnce() {
    const data = await apiFetch(`/warranty/${encodeURIComponent(wrId)}/sales-admin/final-status`, {
      method: "POST",
      body: {},
    });
    setStatus(data);
    return data;
  }

  function computeProgress(st) {
    const s = st?.finalPdfStatus || {};
    const orangeDone = String(s.orange || "").toUpperCase() === "DONE";
    const purpleDone = String(s.purple || "").toUpperCase() === "DONE";
    // Orange is copied => usually DONE immediately; Purple depends on LO convert
    const total = 2;
    const done = (orangeDone ? 1 : 0) + (purpleDone ? 1 : 0);
    return Math.round((done / total) * 100);
  }

  async function pollUntilReady() {
    if (!wrId) return;
    setPolling(true);
    stopRef.current = false;

    const GIVE_UP_AFTER_MS = 3 * 60 * 1000; // 3 min polling
    const started = Date.now();

    try {
      while (!stopRef.current) {
        const st = await fetchStatusOnce();
        if (st?.ready) return;

        if (Date.now() - started > GIVE_UP_AFTER_MS) {
          setOkMsg("Submitted. Still processing PDF conversion. You can refresh this page later to download.");
          return;
        }
        await sleep(2500);
      }
    } finally {
      setPolling(false);
    }
  }

  useEffect(() => {
    return () => {
      stopRef.current = true;
    };
  }, []);

  async function submitFinal() {
    if (!wrId) return setError("Missing wrId in URL.");
    if (!String(purpleFormNumber || "").trim()) return setError("PurpleForm Number (Q1) is required.");

    const ok = window.confirm("This will generate all the files and you will be able to download them here.\n\nContinue?");
    if (!ok) return;

    setBusy(true);
    setError("");
    setOkMsg("");
    setStatus(null);

    try {
      await apiFetch(`/warranty/${encodeURIComponent(wrId)}/sales-admin/final-submit`, {
        method: "POST",
        body: { purpleFormNumber: String(purpleFormNumber || "").trim() },
      });

      setOkMsg("Submitted. Generating Purple PDF…");
      await pollUntilReady();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };
  const progress = computeProgress(status);

  const downloads = status?.downloads || {};
  const ready = !!status?.ready;

  return (
    <div style={{ padding: 24, maxWidth: 860, margin: "0 auto" }}>
      <div style={{ fontSize: 18, fontWeight: 900 }}>Warranty (Sales Admin 2)</div>

      <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
        wrId: <span style={mono}>{wrId || "-"}</span>
      </div>

      {error ? <div style={{ marginTop: 12, color: "#b91c1c", fontWeight: 800 }}>{error}</div> : null}
      {okMsg ? <div style={{ marginTop: 12, color: "#166534", fontWeight: 800 }}>{okMsg}</div> : null}

      <div
  style={{
    marginTop: 14,
    border: "1px solid #e9d5ff", // light purple border
    background: "#faf5ff",       // light purple background
    borderRadius: 12,
    padding: 12,
  }}
>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Request for Delivery</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800 }}>Form Number</div>
            <input
              value={purpleFormNumber}
              disabled={busy || polling}
              onChange={(e) => setPurpleFormNumber(e.target.value)}
              style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #e2e8f0" }}
            />
          </div>
        </div>

        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={submitFinal}
            disabled={busy || polling}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              fontWeight: 900,
              background: "#0f172a",
              color: "white",
              opacity: busy || polling ? 0.7 : 1,
              cursor: busy || polling ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Submitting..." : polling ? "Processing..." : "Submit"}
          </button>

          <button
            onClick={async () => {
              setError("");
              try {
                await fetchStatusOnce();
              } catch (e) {
                setError(e.message || String(e));
              }
            }}
            disabled={busy}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              fontWeight: 900,
              background: "white",
            }}
          >
            Refresh status
          </button>
        </div>

        {/* Progress */}
        {status ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
              Conversion progress: <span style={mono}>{progress}%</span>
              {polling ? " · checking…" : ""}
            </div>

            <div style={{ height: 10, borderRadius: 999, background: "#e2e8f0", overflow: "hidden" }}>
              <div style={{ height: 10, width: `${progress}%`, background: "#0f172a" }} />
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: "#334155" }}>
              Orange PDF: <b>{String(status?.finalPdfStatus?.orange || "-")}</b> · Purple PDF:{" "}
              <b>{String(status?.finalPdfStatus?.purple || "-")}</b>
            </div>
          </div>
        ) : null}
      </div>

      {/* Downloads */}
      {ready ? (
        <div style={{ marginTop: 14, border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
          <div style={{ fontWeight: 900, color: "#166534" }}>Complete files are ready ✅</div>

          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {downloads.orangePdf ? (
              <a href={downloads.orangePdf} target="_blank" rel="noreferrer" style={{ fontWeight: 900 }}>
                Download Orange PDF
              </a>
            ) : null}
            {downloads.orangeXlsx ? (
              <a href={downloads.orangeXlsx} target="_blank" rel="noreferrer" style={{ fontWeight: 900 }}>
                Download Orange XLSX
              </a>
            ) : null}
            {downloads.purplePdf ? (
              <a href={downloads.purplePdf} target="_blank" rel="noreferrer" style={{ fontWeight: 900 }}>
                Download Purple PDF
              </a>
            ) : null}
            {downloads.purpleXlsx ? (
              <a href={downloads.purpleXlsx} target="_blank" rel="noreferrer" style={{ fontWeight: 900 }}>
                Download Purple XLSX
              </a>
            ) : null}
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
            A backup email with the same links will also be sent to your email.
          </div>
        </div>
      ) : null}
    </div>
  );
}
